import { useEffect, useState, memo } from "react";
import { Box, Skeleton, Typography } from "@mui/material";

import { request } from "@/Api";
import { PROJECT_STATUS_CANCELLED } from "@/project/constants";
import {
    StatusBadge,
    WidgetHeaderRow,
    ListRowDivider,
    SeeAllRow,
} from "../primitives";

const colLabelSx = {
    fontSize: 11,
    color: "text.secondary",
    fontFamily: "Inter Tight, sans-serif",
    lineHeight: 1,
    flexShrink: 0,
};
const colTextSx = {
    fontSize: 14,
    color: "text.primary",
    fontFamily: "Inter Tight, sans-serif",
    lineHeight: 1.3,
};
const scrollTextSx = {
    overflow: "auto",
    whiteSpace: "nowrap",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
};

const PROJECT_STATUS_COLOR = (name) => {
    if (!name) return "grey";
    const n = name.toLowerCase();
    if (["brief", "plan", "on radar", "pitching", "briefed"].includes(n))
        return "blue";
    if (["make", "in production", "ongoing"].includes(n)) return "green";
    return "grey";
};

const DashboardProjects = memo(({ personId }) => {
    const [projects, setProjects] = useState(null);
    const [total, setTotal] = useState(null);

    useEffect(() => {
        request
            .get("/statuses")
            .then((statusRes) => {
                const cancelledStatus = statusRes.data.find(
                    (s) => s.name === PROJECT_STATUS_CANCELLED,
                );
                const params = {
                    person_id: personId,
                    limit: 10,
                    orders: "-last_activity",
                };
                if (cancelledStatus) {
                    params.status_id = `neq:${cancelledStatus.id}`;
                }
                return request.get("/projects", { params });
            })
            .then((r) => {
                const data = r.data;
                const items = data.items ?? data ?? [];
                setProjects(Array.isArray(items) ? items : []);
                setTotal(data.total ?? items.length ?? 0);
            })
            .catch(() => {
                setProjects([]);
                setTotal(0);
            });
    }, [personId]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                height: "100%",
            }}
        >
            <WidgetHeaderRow title="Your Recent Projects" count={total} />

            {/* Scrollable table container */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <Box sx={{ minWidth: 480 }}>
                    {/* Column headers */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            px: "8px",
                            gap: "16px",
                            flexShrink: 0,
                            mb: "4px",
                        }}
                    >
                        <Typography
                            sx={{
                                ...colLabelSx,
                                width: "15%",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            Docket
                        </Typography>
                        <Typography
                            sx={{ ...colLabelSx, flex: 1, minWidth: 0 }}
                        >
                            Project
                        </Typography>
                        <Typography
                            sx={{
                                ...colLabelSx,
                                width: "22%",
                                flexShrink: 0,
                            }}
                        >
                            Client
                        </Typography>
                        <Typography
                            sx={{
                                ...colLabelSx,
                                width: "19%",
                                textAlign: "right",
                                flexShrink: 0,
                            }}
                        >
                            Status
                        </Typography>
                    </Box>

                    {/* Rows */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                        }}
                    >
                        {!projects &&
                            [1, 2, 3, 4, 5].map((i) => (
                                <Skeleton
                                    key={i}
                                    variant="rounded"
                                    height={44}
                                    sx={{
                                        borderRadius: "6px",
                                        bgcolor: "action.hover",
                                    }}
                                />
                            ))}
                        {projects?.map((project, i) => (
                            <Box
                                key={project.id || i}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                }}
                            >
                                {i > 0 && <ListRowDivider />}
                                <Box
                                    component="a"
                                    href={`/projects/${project.id}`}
                                    sx={{
                                        display: "flex",
                                        height: 44,
                                        alignItems: "center",
                                        gap: "16px",
                                        px: "8px",
                                        py: "6px",
                                        borderRadius: "6px",
                                        textDecoration: "none",
                                        minWidth: 0,
                                        "&:hover": {
                                            bgcolor: "action.hover",
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            ...colTextSx,
                                            width: "15%",
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {project.docket || "—"}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            ...colTextSx,
                                            flex: 1,
                                            minWidth: 0,
                                            ...scrollTextSx,
                                        }}
                                    >
                                        {project.name}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            ...colTextSx,
                                            width: "22%",
                                            flexShrink: 0,
                                            ...scrollTextSx,
                                        }}
                                    >
                                        {project.client?.name || "—"}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: "19%",
                                            flexShrink: 0,
                                            display: "flex",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <StatusBadge
                                            label={project.status?.name || "—"}
                                            color={PROJECT_STATUS_COLOR(
                                                project.status?.name,
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                        {projects?.length === 0 && (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "text.secondary",
                                    fontFamily: "Inter Tight, sans-serif",
                                    px: "8px",
                                }}
                            >
                                No projects
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            <SeeAllRow href="/projects" label="All projects" />
        </Box>
    );
});

DashboardProjects.displayName = "DashboardProjects";

export default DashboardProjects;
